import { Box, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import type { ChipProps } from '@mui/material';
import { useAppSelector } from '../store/hooks';
import { selectIsAdmin } from '../store/selectors/admin-selectors';
import type { ApprovalStatus } from '../store/slices/admin-slice';

interface AdminEditBadgeProps {
  /** Click handler that opens the per-record edit drawer. */
  readonly onClick: () => void;
  /** Optional aria-label override; defaults to "Edit content". */
  readonly label?: string;
  /**
   * Optional approval status — when supplied, the badge renders a
   * status pill alongside the edit icon so the admin can read it
   * from the Daily page without opening the modal. Pill is hidden
   * when `status` is undefined (e.g., for record types that don't
   * carry an approval lifecycle yet).
   */
  readonly status?: ApprovalStatus;
}

/** Status pill color matches the future MuiChip palette. */
function statusChipColor(status: ApprovalStatus): ChipProps['color'] {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'pending':
    default:
      return 'warning';
  }
}

/**
 * `<AdminEditBadge>` — corner pill rendered ONLY when the current
 * visitor is in admin mode. Renders nothing for non-admin visitors
 * so the entire admin chrome is invisible to the public.
 *
 * Round-16 layout: the badge bundles a status `<Chip>` (pending /
 * approved / rejected) next to the pencil icon button so the
 * admin can read the record's current visibility state directly
 * from the Daily page surface.
 *
 * The host component is responsible for positioning the badge
 * (typically `position: absolute` inside a relatively-positioned
 * card / item wrapper).
 */
export function AdminEditBadge({
  onClick,
  label = 'Edit content',
  status,
}: AdminEditBadgeProps): JSX.Element | null {
  const isAdmin = useAppSelector(selectIsAdmin);
  if (!isAdmin) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 5,
      }}
      data-component="admin-edit-badge"
    >
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        {status && (
          <Chip
            size="small"
            color={statusChipColor(status)}
            label={status.toUpperCase()}
            sx={{ fontWeight: 600, height: 24 }}
          />
        )}
        <Tooltip title={label} arrow>
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClick();
            }}
            sx={{
              backgroundColor: 'primary.main',
              color: 'common.white',
              boxShadow: 2,
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
            aria-label={label}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}
